// Routing Forms: Quiz invitees before routing to correct event type
import React, { useState, useEffect } from 'react';
import { schedulingApi } from '../../services/scheduling-api';
import { EventType } from '../../types/scheduling';
import './SchedulingStyles.css';

interface RoutingForm {
  id: number;
  title: string;
  description?: string;
  slug: string;
  questions: Question[];
  routes: Record<string, number>;
  defaultEventTypeId: number;
}

interface Question {
  id: string;
  type: 'single_choice' | 'multiple_choice' | 'text';
  question: string;
  options: QuestionOption[];
  required: boolean;
}

interface QuestionOption {
  value: string;
  label: string;
  routeToEventTypeId?: number;
}

interface FormData {
  title: string;
  description: string;
  questions: Question[];
  routes: Record<string, number>;
  defaultEventTypeId: number | null;
}

export const RoutingForms: React.FC = () => {
  const [forms, setForms] = useState<RoutingForm[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedFormId, setCopiedFormId] = useState<number | null>(null);

  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    questions: [],
    routes: {},
    defaultEventTypeId: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [formsRes, eventTypesRes] = await Promise.all([
        schedulingApi.get('/routing/forms'),
        schedulingApi.get('/event-types')
      ]);
      setForms(formsRes.data.forms || []);
      setEventTypes(eventTypesRes.data.eventTypes || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (formData.questions.length === 0) {
      setError('At least one question is required');
      return;
    }
    if (!formData.defaultEventTypeId) {
      setError('Default event type is required');
      return;
    }

    try {
      const response = await schedulingApi.post('/routing/forms', {
        title: formData.title,
        description: formData.description,
        questions: formData.questions,
        routes: formData.routes,
        defaultEventTypeId: formData.defaultEventTypeId
      });

      setSuccess(`Routing form created! URL: ${response.data.formUrl}`);
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create routing form');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      questions: [],
      routes: {},
      defaultEventTypeId: null
    });
  };

  const addQuestion = () => {
    const newQuestion: Question = {
      id: `q_${Date.now()}`,
      type: 'single_choice',
      question: '',
      options: [
        { value: 'option_1', label: '' },
        { value: 'option_2', label: '' }
      ],
      required: true
    };
    setFormData({
      ...formData,
      questions: [...formData.questions, newQuestion]
    });
  };

  const updateQuestion = (index: number, field: string, value: any) => {
    const updated = [...formData.questions];
    (updated[index] as any)[field] = value;
    setFormData({ ...formData, questions: updated });
  };

  const removeQuestion = (index: number) => {
    const updated = formData.questions.filter((_, i) => i !== index);
    setFormData({ ...formData, questions: updated });
  };

  const addOption = (questionIndex: number) => {
    const updated = [...formData.questions];
    updated[questionIndex].options.push({
      value: `option_${Date.now()}`,
      label: ''
    });
    setFormData({ ...formData, questions: updated });
  };

  const updateOption = (questionIndex: number, optionIndex: number, field: string, value: any) => {
    const updated = [...formData.questions];
    (updated[questionIndex].options[optionIndex] as any)[field] = value;
    setFormData({ ...formData, questions: updated });
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    const updated = [...formData.questions];
    updated[questionIndex].options = updated[questionIndex].options.filter((_, i) => i !== optionIndex);
    setFormData({ ...formData, questions: updated });
  };

  const setOptionRoute = (questionId: string, optionValue: string, eventTypeId: number | null) => {
    const routeKey = `${questionId}:${optionValue}`;
    const routes = { ...formData.routes };
    if (eventTypeId) {
      routes[routeKey] = eventTypeId;
    } else {
      delete routes[routeKey];
    }
    setFormData({ ...formData, routes });
  };

  const handleDeleteForm = async (formId: number) => {
    if (!window.confirm('Are you sure you want to delete this routing form?')) return;

    try {
      await schedulingApi.delete(`/routing/forms/${formId}`);
      setSuccess('Routing form deleted');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete form');
    }
  };

  const copyFormLink = (formUrl: string, formId: number) => {
    navigator.clipboard.writeText(formUrl);
    setCopiedFormId(formId);
    setTimeout(() => setCopiedFormId(null), 2000);
  };

  if (loading) {
    return <div className="scheduling-loading">Loading routing forms...</div>;
  }

  return (
    <div className="scheduling-section">
      <div className="scheduling-header">
        <h2>Routing Forms</h2>
        <p>Pre-qualify invitees with questions and route them to the right event type</p>
        <button
          className="scheduling-btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          Create Routing Form
        </button>
      </div>

      {error && (
        <div className="scheduling-alert scheduling-alert-error">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className="scheduling-alert scheduling-alert-success">
          {success}
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Routing Forms List */}
      <div className="scheduling-list">
        {forms.length === 0 ? (
          <div className="scheduling-empty">
            <p>No routing forms created yet</p>
            <p>Create a routing form to pre-qualify invitees before booking</p>
          </div>
        ) : (
          forms.map(form => (
            <div key={form.id} className="scheduling-card">
              <div className="scheduling-card-header">
                <h3>{form.title}</h3>
              </div>

              {form.description && (
                <p className="scheduling-card-description">{form.description}</p>
              )}

              <div className="scheduling-card-meta">
                <span>{form.questions.length} question(s)</span>
                <span>Routes to {Object.keys(form.routes).length + 1} event type(s)</span>
              </div>

              <div className="scheduling-card-actions">
                <button
                  className="scheduling-btn-secondary"
                  onClick={() => copyFormLink(
                    `https://cal.larryshelp.org/route/${form.slug}`,
                    form.id
                  )}
                >
                  {copiedFormId === form.id ? 'Copied!' : 'Copy Link'}
                </button>
                <button
                  className="scheduling-btn-danger"
                  onClick={() => handleDeleteForm(form.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Routing Form Modal */}
      {showCreateModal && (
        <div className="scheduling-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="scheduling-modal scheduling-modal-large" onClick={e => e.stopPropagation()}>
            <div className="scheduling-modal-header">
              <h2>Create Routing Form</h2>
              <button
                className="scheduling-modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateForm}>
              <div className="scheduling-form-group">
                <label>Form Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Book the right meeting type"
                />
              </div>

              <div className="scheduling-form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Help us route you to the right meeting..."
                  rows={2}
                />
              </div>

              <div className="scheduling-form-group">
                <label>Default Event Type *</label>
                <select
                  value={formData.defaultEventTypeId || ''}
                  onChange={e => setFormData({
                    ...formData,
                    defaultEventTypeId: e.target.value ? Number(e.target.value) : null
                  })}
                >
                  <option value="">Select default event type...</option>
                  {eventTypes.map(et => (
                    <option key={et.id} value={et.id}>
                      {et.title} ({et.durationMinutes} min)
                    </option>
                  ))}
                </select>
                <small>Fallback if no routing rules match</small>
              </div>

              <div className="scheduling-questions-section">
                <div className="scheduling-questions-header">
                  <h3>Questions</h3>
                  <button
                    type="button"
                    className="scheduling-btn-secondary"
                    onClick={addQuestion}
                  >
                    + Add Question
                  </button>
                </div>

                {formData.questions.length === 0 ? (
                  <p className="scheduling-no-questions">
                    Add questions to route invitees to different event types
                  </p>
                ) : (
                  formData.questions.map((question, qIndex) => (
                    <div key={question.id} className="scheduling-question-card">
                      <div className="scheduling-question-header">
                        <span>Question {qIndex + 1}</span>
                        <button
                          type="button"
                          className="scheduling-btn-icon"
                          onClick={() => removeQuestion(qIndex)}
                        >
                          ×
                        </button>
                      </div>

                      <div className="scheduling-form-group">
                        <label>Question Text *</label>
                        <input
                          type="text"
                          value={question.question}
                          onChange={e => updateQuestion(qIndex, 'question', e.target.value)}
                          placeholder="What would you like to discuss?"
                        />
                      </div>

                      <div className="scheduling-form-row">
                        <div className="scheduling-form-group">
                          <label>Type</label>
                          <select
                            value={question.type}
                            onChange={e => updateQuestion(qIndex, 'type', e.target.value)}
                          >
                            <option value="single_choice">Single Choice</option>
                            <option value="multiple_choice">Multiple Choice</option>
                            <option value="text">Text Input</option>
                          </select>
                        </div>
                        <div className="scheduling-form-group">
                          <label className="scheduling-checkbox">
                            <input
                              type="checkbox"
                              checked={question.required}
                              onChange={e => updateQuestion(qIndex, 'required', e.target.checked)}
                            />
                            Required
                          </label>
                        </div>
                      </div>

                      {(question.type === 'single_choice' || question.type === 'multiple_choice') && (
                        <div className="scheduling-options-section">
                          <label>Options & Routing</label>
                          {question.options.map((option, oIndex) => (
                            <div key={option.value} className="scheduling-option-row">
                              <input
                                type="text"
                                value={option.label}
                                onChange={e => updateOption(qIndex, oIndex, 'label', e.target.value)}
                                placeholder={`Option ${oIndex + 1}`}
                              />
                              <select
                                value={formData.routes[`${question.id}:${option.value}`] || ''}
                                onChange={e => setOptionRoute(
                                  question.id,
                                  option.value,
                                  e.target.value ? Number(e.target.value) : null
                                )}
                              >
                                <option value="">Use default</option>
                                {eventTypes.map(et => (
                                  <option key={et.id} value={et.id}>
                                    → {et.title}
                                  </option>
                                ))}
                              </select>
                              {question.options.length > 2 && (
                                <button
                                  type="button"
                                  className="scheduling-btn-icon"
                                  onClick={() => removeOption(qIndex, oIndex)}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            className="scheduling-btn-text"
                            onClick={() => addOption(qIndex)}
                          >
                            + Add Option
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              <div className="scheduling-modal-actions">
                <button
                  type="button"
                  className="scheduling-btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="scheduling-btn-primary">
                  Create Routing Form
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Public Routing Form Page
export const PublicRoutingFormPage: React.FC<{ subdomain: string; slug: string }> = ({
  subdomain,
  slug
}) => {
  const [form, setForm] = useState<any>(null);
  const [host, setHost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [routedEvent, setRoutedEvent] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchForm();
  }, [subdomain, slug]);

  const fetchForm = async () => {
    try {
      const response = await schedulingApi.get(`/routing/public/${subdomain}/${slug}`);
      setForm(response.data.form);
      setHost(response.data.host);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId: string, value: string) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleNext = () => {
    if (currentQuestion < form.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      const response = await schedulingApi.post(`/routing/public/${subdomain}/${slug}/route`, {
        answers
      });
      setRoutedEvent(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to route');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="scheduling-loading">Loading...</div>;
  }

  if (error && !form) {
    return (
      <div className="scheduling-public-page">
        <div className="scheduling-error-page">
          <h2>Form Not Found</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (routedEvent) {
    return (
      <div className="scheduling-public-page">
        <div className="scheduling-routed-page">
          <h2>Perfect! We found the right meeting type for you.</h2>
          <div className="scheduling-routed-event">
            <h3>{routedEvent.routedToEventType.title}</h3>
            {routedEvent.routedToEventType.description && (
              <p>{routedEvent.routedToEventType.description}</p>
            )}
            <p>{routedEvent.routedToEventType.durationMinutes} minutes</p>
          </div>
          <a
            href={routedEvent.bookingUrl}
            className="scheduling-btn-primary scheduling-btn-full"
          >
            Continue to Booking
          </a>
        </div>
      </div>
    );
  }

  const question = form.questions[currentQuestion];
  const isLastQuestion = currentQuestion === form.questions.length - 1;
  const canProceed = !question.required || answers[question.id];

  return (
    <div className="scheduling-public-page">
      <div className="scheduling-routing-container">
        <div className="scheduling-routing-header">
          {host?.avatarUrl && (
            <img src={host.avatarUrl} alt={host.displayName} className="scheduling-host-avatar" />
          )}
          <h1>{form.title}</h1>
          {form.description && <p>{form.description}</p>}
        </div>

        <div className="scheduling-progress">
          <div
            className="scheduling-progress-bar"
            style={{ width: `${((currentQuestion + 1) / form.questions.length) * 100}%` }}
          />
        </div>

        {error && (
          <div className="scheduling-alert scheduling-alert-error">{error}</div>
        )}

        <div className="scheduling-question-container">
          <h3>{question.question}</h3>

          {question.type === 'single_choice' && (
            <div className="scheduling-options">
              {question.options.map((option: QuestionOption) => (
                <label
                  key={option.value}
                  className={`scheduling-option ${answers[question.id] === option.value ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name={question.id}
                    value={option.value}
                    checked={answers[question.id] === option.value}
                    onChange={() => handleAnswer(question.id, option.value)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          )}

          {question.type === 'multiple_choice' && (
            <div className="scheduling-options">
              {question.options.map((option: QuestionOption) => (
                <label
                  key={option.value}
                  className={`scheduling-option ${(answers[question.id] || '').includes(option.value) ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    value={option.value}
                    checked={(answers[question.id] || '').includes(option.value)}
                    onChange={e => {
                      const current = answers[question.id]?.split(',') || [];
                      const updated = e.target.checked
                        ? [...current, option.value]
                        : current.filter(v => v !== option.value);
                      handleAnswer(question.id, updated.filter(Boolean).join(','));
                    }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          )}

          {question.type === 'text' && (
            <textarea
              value={answers[question.id] || ''}
              onChange={e => handleAnswer(question.id, e.target.value)}
              placeholder="Type your answer..."
              rows={4}
            />
          )}
        </div>

        <div className="scheduling-navigation">
          {currentQuestion > 0 && (
            <button
              type="button"
              className="scheduling-btn-secondary"
              onClick={handleBack}
            >
              Back
            </button>
          )}

          {isLastQuestion ? (
            <button
              type="button"
              className="scheduling-btn-primary"
              onClick={handleSubmit}
              disabled={!canProceed || submitting}
            >
              {submitting ? 'Finding your meeting...' : 'Continue'}
            </button>
          ) : (
            <button
              type="button"
              className="scheduling-btn-primary"
              onClick={handleNext}
              disabled={!canProceed}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoutingForms;
